import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { addDays, format, isWithinInterval } from "date-fns";
import { HTTP_STATUS_CODES, SDP_ROTATION_DATE_FORMAT } from "app/constants";
import {
	MIN_CALENDAR_DATE_OFFSET,
	MAX_CALENDAR_DATE_OFFSET,
	MIN_BOOKING_DATE_OFFSET,
	MAX_BOOKING_DATE_OFFSET,
} from "../constants/travelDates";
import { TravelMode } from "../types";

type DateLike = string | Date;
type CalendarDateInput = string | Date | number;

type AvailabilityItem = {
	duration?: number;
	ed?: string;
};

type AvailabilitiesByDate = Record<string, AvailabilityItem[]>;

type UseSDPTravelDatesAvailabilityParams = {
	travelMode: TravelMode;
	uri?: string;
	departureCity?: string;
	destinationResort?: string;
	departureDate?: DateLike;
	returnDate?: DateLike;
	availabilities?: AvailabilitiesByDate;
	fetchAvailability: (uri: string) => unknown;
	resetAvailability: () => void;
	clearDates?: () => void;
};

const toDate = (value?: DateLike) => {
	if (!value) {
		return undefined;
	}
	return new Date(value);
};

const toDateKey = (value: CalendarDateInput | Date) => {
	return format(new Date(value), SDP_ROTATION_DATE_FORMAT);
};

const isEmptyObject = (value: Record<string, unknown>) => {
	return Object.keys(value).length === 0;
};

const getMinDateKey = (keys: string[]) => {
	if (!keys.length) {
		return undefined;
	}

	return keys.reduce((minKey, key) => {
		return key < minKey ? key : minKey;
	}, keys[0]);
};

const getMaxDateKey = (keys: string[]) => {
	if (!keys.length) {
		return undefined;
	}

	return keys.reduce((maxKey, key) => {
		return key > maxKey ? key : maxKey;
	}, keys[0]);
};

const getLongestStay = (options: AvailabilityItem[]) => {
	if (!options.length) {
		return undefined;
	}

	return options.reduce((longest, option) => {
		const longestDuration = longest && longest.duration ? longest.duration : 0;
		const optionDuration = option && option.duration ? option.duration : 0;
		return optionDuration > longestDuration ? option : longest;
	}, options[0]);
};

const computeMaxDateKey = ({
	departure,
	returnDate,
	availabilities,
	hasError,
	defaultMaxDateKey,
	maxReturnDate,
}: {
	departure?: Date;
	returnDate?: DateLike;
	availabilities: AvailabilitiesByDate;
	hasError: boolean;
	defaultMaxDateKey?: string;
	maxReturnDate?: Date;
}) => {
	if (!departure || isEmptyObject(availabilities)) {
		return defaultMaxDateKey;
	}

	const departureKey = toDateKey(departure);
	const returnOptions = availabilities[departureKey];

	if (!returnDate && returnOptions) {
		const longestStay = getLongestStay(returnOptions);
		return longestStay && longestStay.ed;
	}

	if (!returnDate && hasError && maxReturnDate) {
		return toDateKey(maxReturnDate);
	}

	return defaultMaxDateKey;
};

const isDateDisabledWithAvailabilities = ({
	date,
	departureDate,
	returnDate,
	availabilities,
}: {
	date: CalendarDateInput;
	departureDate?: DateLike;
	returnDate?: DateLike;
	availabilities: AvailabilitiesByDate;
}) => {
	const currentDateKey = toDateKey(date);

	if (departureDate && !returnDate) {
		const departureKey = toDateKey(departureDate);
		const returnOptions = availabilities[departureKey] || [];

		const isValidEndDate = returnOptions.some(option => option.ed === currentDateKey);

		const isValidAnteriorDepartureDate =
			currentDateKey <= departureKey && Object.keys(availabilities).includes(currentDateKey);

		return !(isValidEndDate || isValidAnteriorDepartureDate);
	}

	return !Object.keys(availabilities).includes(currentDateKey);
};

const isDateDisabledWithErrorFallback = ({
	date,
	departureDate,
	maxReturnDate,
}: {
	date: CalendarDateInput;
	departureDate: DateLike;
	maxReturnDate?: Date;
}) => {
	const maxDateTs = maxReturnDate ? maxReturnDate.getTime() : undefined;
	if (!maxDateTs) {
		return true;
	}

	const currentDateTs = new Date(date).getTime();
	const minDateTs = addDays(new Date(departureDate), MIN_BOOKING_DATE_OFFSET).getTime();

	return !(currentDateTs >= minDateTs && currentDateTs <= maxDateTs);
};

export const FETCH_AVAILABILITIES_STATUS = {
	SUCCESS: "success",
	ERROR: "error",
	LOADING: "loading",
};

export const useSDPTravelDatesAvailability = ({
	travelMode,
	uri,
	departureCity,
	destinationResort,
	departureDate,
	returnDate,
	availabilities = {},
	fetchAvailability,
	resetAvailability,
	clearDates = () => {},
}: UseSDPTravelDatesAvailabilityParams) => {
	const [availabilitiesStatus, setAvailabilitiesStatus] = useState(
		FETCH_AVAILABILITIES_STATUS.LOADING
	);

	const hasError = availabilitiesStatus === FETCH_AVAILABILITIES_STATUS.ERROR;

	const latestRequestIdRef = useRef(0);

	const today = useMemo(() => new Date(), []);
	const departure = toDate(departureDate);
	const hasAvailabilities = !isEmptyObject(availabilities);

	const fallbackMinDateKey = toDateKey(addDays(today, MIN_CALENDAR_DATE_OFFSET));
	const fallbackMaxDateKey = toDateKey(addDays(today, MAX_CALENDAR_DATE_OFFSET));

	const availableDepartureDates = useMemo(() => Object.keys(availabilities), [availabilities]);

	const minDateKey =
		availableDepartureDates.length > 0 && !hasError
			? getMinDateKey(availableDepartureDates)
			: fallbackMinDateKey;

	const defaultMaxDateKey =
		availableDepartureDates.length > 0 && !hasError
			? getMaxDateKey(availableDepartureDates)
			: fallbackMaxDateKey;

	const maxReturnDate = departure ? addDays(departure, MAX_BOOKING_DATE_OFFSET) : undefined;

	const maxDateKey = computeMaxDateKey({
		departure,
		returnDate,
		availabilities,
		hasError,
		defaultMaxDateKey,
		maxReturnDate,
	});

	useEffect(() => {
		if (travelMode !== "sdp") {
			return;
		}

		if (!uri) {
			setAvailabilitiesStatus(FETCH_AVAILABILITIES_STATUS.SUCCESS);
			resetAvailability();
			if (departureCity || destinationResort) {
				clearDates();
			}
			return;
		}

		if (!departureCity || !destinationResort) {
			return;
		}

		latestRequestIdRef.current += 1;
		const requestId = latestRequestIdRef.current;

		setAvailabilitiesStatus(FETCH_AVAILABILITIES_STATUS.LOADING);

		const loadAvailability = async () => {
			try {
				const response = (await fetchAvailability(uri)) as {
					status?: number;
					data?: unknown;
				};

				if (requestId !== latestRequestIdRef.current) {
					return;
				}

				const responseStatus = response && response.status;

				if (responseStatus !== HTTP_STATUS_CODES.OK) {
					setAvailabilitiesStatus(FETCH_AVAILABILITIES_STATUS.ERROR);
					return;
				}

				setAvailabilitiesStatus(FETCH_AVAILABILITIES_STATUS.SUCCESS);

				if (isEmptyObject((response && (response.data as Record<string, unknown>)) || {})) {
					clearDates();
				}
			} catch {
				if (requestId !== latestRequestIdRef.current) {
					return;
				}

				resetAvailability();
				setAvailabilitiesStatus(FETCH_AVAILABILITIES_STATUS.ERROR);
			}
		};

		loadAvailability();
	}, [
		travelMode,
		uri,
		departureCity,
		destinationResort,
		fetchAvailability,
		resetAvailability,
		clearDates,
	]);

	useEffect(() => {
		if (
			travelMode !== "sdp" ||
			!uri ||
			!hasAvailabilities ||
			hasError ||
			!minDateKey ||
			!maxDateKey ||
			!departureDate
		) {
			return;
		}

		const departureKey = toDateKey(departureDate);
		const rangeStart = new Date(minDateKey);
		const rangeEnd = new Date(maxDateKey);

		const departureKeysInCalendarRange = Object.keys(availabilities).filter(dateKey =>
			isWithinInterval(new Date(dateKey), { start: rangeStart, end: rangeEnd })
		);

		if (!departureKeysInCalendarRange.includes(departureKey)) {
			clearDates();
			return;
		}

		const returnOptions = availabilities[departureKey];

		if (!returnOptions || returnOptions.length === 0) {
			clearDates();
			return;
		}

		if (returnDate) {
			const endKey = toDateKey(returnDate);
			const endStillValid = returnOptions.some(option => option.ed === endKey);

			if (!endStillValid) {
				clearDates();
			}
		}
	}, [
		travelMode,
		uri,
		availabilities,
		hasAvailabilities,
		hasError,
		minDateKey,
		maxDateKey,
		departureDate,
		returnDate,
		clearDates,
	]);

	const isDateDisabled = useCallback(
		(date?: CalendarDateInput) => {
			if (!date || (!hasAvailabilities && !hasError)) {
				return true;
			}

			if (hasAvailabilities && !hasError) {
				return isDateDisabledWithAvailabilities({
					date,
					departureDate,
					returnDate,
					availabilities,
				});
			}

			if (!hasAvailabilities && hasError && departureDate && !returnDate) {
				return isDateDisabledWithErrorFallback({
					date,
					departureDate,
					maxReturnDate,
				});
			}

			return false;
		},
		[availabilities, departureDate, returnDate, hasAvailabilities, hasError, maxReturnDate]
	);

	return {
		minDate: minDateKey ? new Date(minDateKey).getTime() : undefined,
		maxDate: maxDateKey ? new Date(maxDateKey).getTime() : undefined,
		availabilitiesStatus,
		isDateDisabled,
	};
};
